import { useSyncExternalStore } from 'react'

/**
 * Routing, without a router.
 *
 * Five paths, one numeric parameter each at most. A route table and a
 * dependency would be more code than the `match` below, which is the whole of
 * it. The server falls unknown paths back to `index.html`, so every URL here
 * works on a cold load and a refresh as well as on a tap.
 */
export type Route =
  | { name: 'pipeline' }
  | { name: 'idea'; id: number }
  | { name: 'linkedin' }
  | { name: 'film-session'; batchId: number }
  | { name: 'search' }
  | { name: 'not-found' }

export function matchRoute(path: string): Route {
  const clean = path.replace(/\/+$/, '') || '/'
  if (clean === '/') return { name: 'pipeline' }
  if (clean === '/linkedin') return { name: 'linkedin' }
  if (clean === '/search') return { name: 'search' }
  const idea = /^\/idea\/(\d+)$/.exec(clean)
  if (idea) return { name: 'idea', id: Number(idea[1]) }
  const session = /^\/film\/(\d+)$/.exec(clean)
  if (session) return { name: 'film-session', batchId: Number(session[1]) }
  return { name: 'not-found' }
}

/** `pushState` announces nothing, so navigation fires this for subscribers. */
const NAVIGATED = 'content:navigated'

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange)
  window.addEventListener(NAVIGATED, onChange)
  return () => {
    window.removeEventListener('popstate', onChange)
    window.removeEventListener(NAVIGATED, onChange)
  }
}

export function usePath(): string {
  return useSyncExternalStore(
    subscribe,
    () => window.location.pathname,
    () => '/',
  )
}

/**
 * Where each history entry was scrolled to, so Back lands on the row you
 * left from rather than the top of a forty-row list.
 */
const scrollPositions = new Map<string, number>()
let lastNavigationWasPop = false

if (typeof window !== 'undefined') {
  window.history.scrollRestoration = 'manual'
  window.addEventListener('popstate', () => {
    lastNavigationWasPop = true
  })
}

function entryKey(): string {
  const state = window.history.state as { key?: string } | null
  return state?.key ?? window.location.pathname
}

export function navigate(destination: string, { replace = false } = {}): void {
  if (destination === window.location.pathname + window.location.search) return
  scrollPositions.set(entryKey(), window.scrollY)
  const state = { key: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}` }
  if (replace) window.history.replaceState(state, '', destination)
  else window.history.pushState(state, '', destination)
  lastNavigationWasPop = false
  window.dispatchEvent(new Event(NAVIGATED))
}

/** Back within the app when there is somewhere to go back to, home otherwise. */
export function goBack(fallback = '/'): void {
  const state = window.history.state as { key?: string } | null
  if (state?.key && window.history.length > 1) window.history.back()
  else navigate(fallback, { replace: true })
}

/**
 * Call after a route has rendered. Restores the scroll of an entry returned
 * to with Back, and starts a fresh one at the top.
 */
export function restoreScroll(): void {
  const saved = lastNavigationWasPop ? scrollPositions.get(entryKey()) : undefined
  window.scrollTo(0, saved ?? 0)
  lastNavigationWasPop = false
}

/** True for the click that means "go there in this tab"; everything else is the browser's. */
export function isPlainLeftClick(event: {
  button: number
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
  defaultPrevented: boolean
}): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  )
}
